import React, { useState, useRef, useEffect, useCallback, Suspense } from 'react';
import { createPortal } from 'react-dom';
import { IonIcon } from '@ionic/react';
import { locationOutline, close, mapOutline, checkmarkCircleOutline } from 'ionicons/icons';
import { geocodeAddress, type GeocodeSuggestion } from '@/utils/geocode';

const ManualAddressMap = React.lazy(() => import('./ManualAddressMap'));

/* ── Parsed address from a geocode result ── */
export interface ParsedAddress {
  address: string;
  city: string;
  latitude: number;
  longitude: number;
  displayName: string;
}

interface AddressAutocompleteProps {
  value: string;
  placeholder?: string;
  onSelect: (result: ParsedAddress) => void;
  onClear?: () => void;
  /** Called on every keystroke so the parent form tracks the raw typed value */
  onManualChange?: (value: string) => void;
  countryCode?: string;
  className?: string;
  hasError?: boolean;
  /** Enable the manual address fallback panel. Defaults to true. */
  enableManualFallback?: boolean;
}

const DEBOUNCE_MS = 400;
const MIN_CHARS = 3;

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  value,
  placeholder = 'Cauta adresa...',
  onSelect,
  onClear,
  onManualChange,
  className,
  hasError,
  enableManualFallback = true,
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [highlightIndex, setHighlightIndex] = useState(-1);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, openAbove: false });

  // Manual fallback state
  const [noResults, setNoResults] = useState(false);
  const [showManualPanel, setShowManualPanel] = useState(false);
  const [manualAddress, setManualAddress] = useState('');
  const [manualCity, setManualCity] = useState('');
  const [manualPin, setManualPin] = useState<[number, number] | null>(null);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);
  const isFocusedRef = useRef(false);

  // Sync external value — only when the input is NOT focused (avoids feedback loop)
  useEffect(() => {
    if (!isFocusedRef.current) {
      setInputValue(value);
    }
  }, [value]);

  // Calculate dropdown position from input element, flipping above if near viewport bottom
  const updateDropdownPos = useCallback(() => {
    if (wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownMaxHeight = 280; // matches CSS max-height
      const openAbove = spaceBelow < dropdownMaxHeight + 8;
      setDropdownPos({
        top: openAbove ? rect.top - 4 : rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        openAbove,
      });
    }
  }, []);

  // Click outside or scroll to close
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as Node;
      if (wrapperRef.current && !wrapperRef.current.contains(target)) {
        // Also check if clicked inside the portal dropdown
        const dropdown = document.getElementById('address-dropdown-portal');
        if (dropdown && dropdown.contains(target)) return;
        setIsOpen(false);
      }
    };
    const handleScroll = () => {
      if (isOpen) updateDropdownPos();
    };
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleScroll, true);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScroll, true);
    };
  }, [isOpen, updateDropdownPos]);

  const fetchSuggestions = useCallback(async (query: string) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsLoading(true);
    try {
      const data = await geocodeAddress(query, controller.signal);
      setSuggestions(data);
      if (data.length > 0) {
        updateDropdownPos();
        setIsOpen(true);
        setNoResults(false);
      } else {
        setIsOpen(false);
        setNoResults(true);
      }
      setHighlightIndex(-1);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setSuggestions([]);
        setIsOpen(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [updateDropdownPos]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setInputValue(val);
    onManualChange?.(val);

    if (showManualPanel) setShowManualPanel(false);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (val.length < MIN_CHARS) {
      setSuggestions([]);
      setIsOpen(false);
      setNoResults(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      void fetchSuggestions(val);
    }, DEBOUNCE_MS);
  };

  const handleSelect = (result: GeocodeSuggestion) => {
    const parsed: ParsedAddress = {
      address: result.address,
      city: result.city,
      latitude: result.latitude,
      longitude: result.longitude,
      displayName: result.displayName,
    };
    const display = [parsed.address, parsed.city].filter(Boolean).join(', ');
    setInputValue(display || result.displayName);
    setIsOpen(false);
    setSuggestions([]);
    setNoResults(false);
    setShowManualPanel(false);
    onSelect(parsed);
  };

  const handleClear = () => {
    setInputValue('');
    setSuggestions([]);
    setIsOpen(false);
    setNoResults(false);
    setShowManualPanel(false);
    resetManualFields();
    onClear?.();
    inputRef.current?.focus();
  };

  const resetManualFields = () => {
    setManualAddress('');
    setManualCity('');
    setManualPin(null);
  };

  const openManualPanel = () => {
    setShowManualPanel(true);
    setNoResults(false);
    // Pre-fill manual address with what the user typed
    setManualAddress(inputValue);
  };

  const closeManualPanel = () => {
    setShowManualPanel(false);
    resetManualFields();
  };

  const handleManualConfirm = () => {
    if (!manualAddress.trim() || !manualPin) return;

    const parsed: ParsedAddress = {
      address: manualAddress.trim(),
      city: manualCity.trim(),
      latitude: manualPin[0],
      longitude: manualPin[1],
      displayName: [manualAddress.trim(), manualCity.trim()].filter(Boolean).join(', '),
    };

    const display = parsed.displayName;
    setInputValue(display);
    setShowManualPanel(false);
    setNoResults(false);
    resetManualFields();
    onSelect(parsed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIndex((prev) => Math.max(prev - 1, 0));
    } else if (e.key === 'Enter' && highlightIndex >= 0 && suggestions[highlightIndex]) {
      e.preventDefault();
      handleSelect(suggestions[highlightIndex]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  // Render dropdown via portal so it's never clipped by parent overflow
  const dropdown = isOpen && suggestions.length > 0
    ? createPortal(
        <ul
          id="address-dropdown-portal"
          className="address-autocomplete__dropdown"
          style={{
            position: 'fixed',
            ...(dropdownPos.openAbove
              ? { bottom: window.innerHeight - dropdownPos.top, top: 'auto' }
              : { top: dropdownPos.top }),
            left: dropdownPos.left,
            width: dropdownPos.width,
          }}
        >
          {suggestions.map((item, idx) => (
            <li
              key={item.id}
              className={`address-autocomplete__item${idx === highlightIndex ? ' address-autocomplete__item--active' : ''}`}
              onMouseEnter={() => setHighlightIndex(idx)}
              onMouseDown={(e) => { e.preventDefault(); handleSelect(item); }}
            >
              <IonIcon icon={locationOutline} className="address-autocomplete__item-icon" />
              <div className="address-autocomplete__item-text">
                <span className="address-autocomplete__item-main">
                  {item.address || item.displayName.split(',')[0]}
                </span>
                <span className="address-autocomplete__item-secondary">
                  {item.city}
                  {item.source === 'geoapify' && item.confidence === 'high' && ' ★'}
                </span>
              </div>
            </li>
          ))}
          <li className="address-autocomplete__attribution">
            Powered by OpenStreetMap + Geoapify
          </li>
        </ul>,
        document.body,
      )
    : null;

  return (
    <div ref={wrapperRef} className={`address-autocomplete ${className ?? ''}`}>
      <div className={`address-autocomplete__input-wrap${hasError ? ' address-autocomplete__input-wrap--error' : ''}`}>
        <IonIcon icon={locationOutline} className="address-autocomplete__icon" />
        <input
          ref={inputRef}
          type="text"
          className="address-autocomplete__input"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            isFocusedRef.current = true;
            if (suggestions.length > 0) {
              updateDropdownPos();
              setIsOpen(true);
            }
          }}
          onBlur={() => {
            isFocusedRef.current = false;
          }}
          placeholder={placeholder}
          autoComplete="off"
        />
        {isLoading && <div className="address-autocomplete__spinner" />}
        {inputValue && !isLoading && (
          <button type="button" className="address-autocomplete__clear" onClick={handleClear}>
            <IonIcon icon={close} />
          </button>
        )}
      </div>
      {dropdown}

      {/* Manual fallback link — shown whenever user has typed something */}
      {inputValue.trim().length >= MIN_CHARS && !isOpen && !showManualPanel && enableManualFallback && (
        <div className="address-autocomplete__no-results">
          {noResults && <span>Nu am găsit adresa.</span>}
          <button type="button" className="address-autocomplete__no-results-link" onClick={openManualPanel}>
            {noResults ? 'Adaugă manual' : 'Nu găsești adresa? Adaugă manual'}
          </button>
        </div>
      )}

      {/* Manual address panel */}
      {showManualPanel && (
        <div className="address-manual">
          <div className="address-manual__header">
            <span className="address-manual__title">
              <IonIcon icon={mapOutline} style={{ fontSize: 16, verticalAlign: -2, marginRight: 6 }} />
              Adresă manuală
            </span>
            <button type="button" className="address-manual__close" onClick={closeManualPanel}>
              <IonIcon icon={close} />
            </button>
          </div>

          <div className="address-manual__fields">
            <div className="address-manual__field">
              <label>Strada și numărul</label>
              <input
                type="text"
                value={manualAddress}
                onChange={(e) => setManualAddress(e.target.value)}
                placeholder="ex: Strada Unirii 15"
                autoFocus
              />
            </div>
            <div className="address-manual__field">
              <label>Orașul</label>
              <input
                type="text"
                value={manualCity}
                onChange={(e) => setManualCity(e.target.value)}
                placeholder="ex: Arad"
              />
            </div>
          </div>

          <Suspense fallback={<div className="address-manual__map-loading">Se încarcă harta...</div>}>
            <ManualAddressMap
              position={manualPin}
              onPositionChange={(lat, lng) => setManualPin([lat, lng])}
            />
          </Suspense>

          <div className="address-manual__map-hint">
            {manualPin ? (
              <span className="address-manual__map-hint--placed">
                <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 14, verticalAlign: -2, marginRight: 4 }} />
                {manualPin[0].toFixed(5)}, {manualPin[1].toFixed(5)}
                <button type="button" className="address-manual__coords-clear" onClick={() => setManualPin(null)}>
                  <IonIcon icon={close} />
                </button>
              </span>
            ) : (
              <span>
                <IonIcon icon={locationOutline} style={{ fontSize: 14, verticalAlign: -2, marginRight: 4 }} />
                Click pe hartă pentru a plasa pinul
              </span>
            )}
          </div>

          <div className="address-manual__confirm">
            <button
              type="button"
              onClick={handleManualConfirm}
              disabled={!manualAddress.trim() || !manualPin}
            >
              <IonIcon icon={checkmarkCircleOutline} style={{ fontSize: 16 }} />
              Confirmă adresa
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;
